import React from "react"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { KpiMetric, useKPIsData } from "@/hooks/useKPIsData"
import { DateFilter } from "@/types/filters"

interface KPIsTabProps {
  dateFilter?: DateFilter
  jobId?: string
}

export const KPIsTab: React.FC<KPIsTabProps> = ({ dateFilter, jobId }) => {
  // Fetch data from KPIs cloud function
  const { data, isLoading, error } = useKPIsData(dateFilter, jobId)

  // Helper function to get progress value for progress bar
  const getProgressValue = (kpi: KpiMetric) => {
    if (kpi.value === null || kpi.target === null) return 0

    // For metrics where lower is better (like time to hire)
    if (kpi.name === "Time to hire") {
      const value = kpi.value as number
      const target = kpi.target as number
      return value > target ? 0 : ((target - value) / target) * 100
    }

    // For other metrics (higher is better)
    const value = kpi.value as number
    const target = kpi.target as number
    return Math.min(100, (value / target) * 100)
  }

  // Helper function to determine progress color based on achievement level
  const getProgressColor = (kpi: KpiMetric): string => {
    if (kpi.value === null || kpi.target === null) return "bg-palette-jungleGreen200"

    // For time to hire (lower is better, good if below 14 days)
    if (kpi.name === "Time to hire") {
      const value = kpi.value as number
      const targetDays = 14 // Hardcoded target of 14 days

      if (value <= targetDays * 0.8) return "bg-green-500" // Below 14 days (good)
      if (value <= targetDays) return "bg-orange-500" // Within 20% of 14 days (close)
      return "bg-red-500" // More than 20% above 14 days (bad)
    }

    // For trial blocks no-show rate (lower is better, good if below 10%)
    if (kpi.name === "Trial Blocks No Show Rate") {
      const value = kpi.value as number
      const targetRate = 10 // Hardcoded target of 10%
      if (value <= targetRate * 0.8) return "bg-green-500" // Below 10% (good)
      if (value <= targetRate) return "bg-orange-500" // Within 30% of 10% (close)
      return "bg-red-500" // More than 30% above 10% (bad)
    }

    // For other metrics (higher is better)
    const value = kpi.value as number
    const target = kpi.target as number
    const percentage = (value / target) * 100

    if (percentage >= 100) return "bg-green-500" // Met or exceeded target (good)
    if (percentage >= 80) return "bg-orange-500" // Within 80% of target (close)
    return "bg-red-500" // Below 80% of target (bad)
  }

  // Helper function to determine if we should show a progress bar
  const shouldShowProgressBar = (kpi: KpiMetric): boolean => {
    // Don't show progress bar for actual applications (it's just a count)
    if (kpi.name === "Actual applications") return false

    // Don't show progress bar for time to hire (we'll use a different indicator)
    if (kpi.name === "Time to hire") return false

    return kpi.value !== null
  }

  // Helper function to format KPI value based on metric type
  const formatKpiValue = (kpi: KpiMetric) => {
    if (kpi.value === null) {
      return "-"
    } else if (kpi.name === "Time to hire") {
      return `${kpi.value.toFixed(1)} days`
    } else if (kpi.name.includes("rate") || kpi.name.includes("Rate")) {
      return `${kpi.value.toFixed(1)}%`
    } else if (kpi.name === "Orientation quiz score") {
      return `${kpi.value.toFixed(1)}%`
    } else {
      return kpi.value.toLocaleString()
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-500">Error Loading KPIs</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Failed to load KPI data. Please try again later.</p>
          <p className="text-sm text-palette-jungleGreen500">{error.message}</p>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return <Skeleton className="w-full h-96" />
  }

  // Get all KPI metrics from the data
  const kpis = [
    data.actualApplications,
    data.timeToHire,
    data.trialPassRate,
    data.orientationAttendanceRate,
    data.orientationQuizScore,
    data.vehicleCollection,
    data.trialReviewsCompletion,
    data.trialBlocksNoShowRate,
  ]
  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Key Performance Indicators</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi, index) => {
          // We only need the progress value now that status indicators are removed
          const progressValue = getProgressValue(kpi)

          return (
            <Card key={index} className="border">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{kpi.name}</CardTitle>
                    <CardDescription className="text-xs mt-1">{kpi.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-2">
                <div className="flex justify-between items-baseline mb-1">
                  <div className="text-3xl font-bold">{formatKpiValue(kpi)}</div>
                  {kpi.name !== "Actual applications" &&
                    kpi.target !== null &&
                    kpi.value !== null && (
                      <div className="text-sm text-palette-jungleGreen500">
                        Target:{" "}
                        {typeof kpi.target === "number"
                          ? kpi.name === "Time to hire"
                            ? `< ${kpi.target} days`
                            : `${kpi.target}%`
                          : kpi.target}
                      </div>
                    )}
                </div>
                {shouldShowProgressBar(kpi) ? (
                  <Progress value={progressValue} className={`h-2 mt-2 ${getProgressColor(kpi)}`} />
                ) : kpi.name === "Time to hire" && kpi.value !== null && kpi.target !== null ? (
                  <div className="flex items-center mt-2">
                    <div className={`h-3 w-3 rounded-full mr-2 ${getProgressColor(kpi)}`}></div>
                    <span className="text-sm">
                      {(kpi.value as number) <= (kpi.target as number)
                        ? "Within target"
                        : (kpi.value as number) <= (kpi.target as number) * 1.2
                          ? "Slightly above target"
                          : "Above target"}
                    </span>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default KPIsTab
